/**
 * Section-level DXF parsing — parseTablesSection, parseObjectsSection,
 * parseBlocksSection, and individual table entry parsers.
 *
 * Extracted from parser.ts — pure structural refactor.
 */

import {
  type DxfBlockDefinition,
  type DxfBlockRecordEntry,
  type DxfBlocksSection,
  type DxfDictionaryEntry,
  type DxfDimensionStyleEntry,
  type DxfDocument,
  type DxfDynamicBlockObject,
  type DxfEntity,
  type DxfGroup,
  type DxfLayerEntry,
  type DxfLayoutEntry,
  type DxfLinetypeElement,
  type DxfLinetypeEntry,
  type DxfMultileaderStyleEntry,
  type DxfObjectsSection,
  type DxfPoint3D,
  type DxfTablesSection,
  type DxfTextStyleEntry,
  type DxfXDataEntry,
  isSupportedDynamicBlockType,
  isSupportedEntityType,
} from "./types";
// NOTE: splitEntities and parseSupportedEntity must be imported directly
// even though parser.ts also re-exports them — see CLAUDE.md "split file
// imports" rule. A local use requires a local import.
import { parseSupportedEntity, splitEntities } from "./entityParser";
import { foldComplexEntities } from "./entityGrouping";
// T9 PR-3 — symmetric `\U+XXXX` decode for symbol-table names (layer / block /
// style / linetype / dimstyle / blockrecord) + XDATA strings, so a doc exported
// with the writer's legacy-codepage name escaping re-imports with real-character
// names. Names are handle-resolution KEYS — the entity layer/block reference
// (decoded via entityParser.internTrim) must match the table record's name.
import { decodeDxfUnicode, decodeXdataGroupValue } from "./dxfUnicodeEscape";
import {
  type AcDbScale,
  type ParsedAnnotativeRecord,
  dispatchObjectsAnnotativeRecord,
  isAcDbScaleType,
  isAnnotScaleContextDataType,
  parseAcadAnnotativeDecomposition,
  parseAcDbScale,
  parseAcDbScaleListDict,
} from "./objects/objectsAnnotativeParser";
import {
  type DxfAssocArrayObject,
  computeAssocArrayCoreHandles,
  decodeAssocArrayObject,
  isAssocArrayGraphCandidateType,
  isAssocArrayStrictObjectType,
  readAssocObjectOwner,
} from "./assocArrayParser";
import {
  ASSOC_ARRAY_PLACEHOLDER_CODE,
  ASSOC_ARRAY_PLACEHOLDER_INDEX_PREFIX,
} from "./assocArrayPlaceholder";
import {
  type DxfSpatialFilter,
  isSpatialFilterType,
  parseSpatialFilterEntry,
} from "./objects/spatialFilterParser";
import {
  SPATIAL_FILTER_PLACEHOLDER_CODE,
  SPATIAL_FILTER_PLACEHOLDER_INDEX_PREFIX,
} from "./objects/spatialFilterTypes";
import { normalizeHandle } from "./handleNormalize";
import { trackNestedBlockDepth } from "./nestedGroupDepth";
import { StringPool } from "./stringPool";
import { advanceControlDepth } from "./controlBlockDepth";

// Codes follow the Autodesk DXF reference for DIMSTYLE table records.
// String-valued codes (DIMPOST, DIMAPOST, DIMTXSTY, DIMBLK, ...) are
// listed separately in DIMVAR_STRING_CODES so the parser knows to keep
// them as strings instead of converting to numbers.

const DIMVAR_CODE_MAP: Record<number, string> = {
  // Real-number variables
  40: "DIMSCALE",
  41: "DIMASZ",
  42: "DIMEXO",
  43: "DIMDLI",
  44: "DIMEXE",
  45: "DIMRND",
  46: "DIMDLE",
  47: "DIMTP",
  48: "DIMTM",
  140: "DIMTXT",
  141: "DIMCEN",
  142: "DIMTSZ",
  143: "DIMALTF",
  144: "DIMLFAC",
  145: "DIMTVP",
  146: "DIMTFAC",
  147: "DIMGAP",
  148: "DIMALTRND",
  // Color indices
  176: "DIMCLRD",
  177: "DIMCLRE",
  178: "DIMCLRT",
  // Integer codes
  179: "DIMADEC",
  271: "DIMDEC",
  272: "DIMTDEC",
  273: "DIMALTU",
  274: "DIMALTTD",
  275: "DIMAUNIT",
  276: "DIMFRAC",
  277: "DIMLUNIT",
  278: "DIMDSEP",
  279: "DIMTMOVE",
  // Boolean/integer codes (stored as 0/1)
  71: "DIMTOL",
  72: "DIMLIM",
  73: "DIMTIH",
  74: "DIMTOH",
  75: "DIMSE1",
  76: "DIMSE2",
  77: "DIMTAD",
  78: "DIMZIN",
  79: "DIMAZIN",
  170: "DIMALT",
  171: "DIMALTD",
  172: "DIMTOFL",
  173: "DIMSAH",
  174: "DIMTIX",
  175: "DIMSOXD",
  280: "DIMJUST",
  281: "DIMSD1",
  282: "DIMSD2",
  283: "DIMTOLJ",
  284: "DIMTZIN",
  285: "DIMALTZ",
  286: "DIMALTTZ",
  288: "DIMUPT",
  289: "DIMATFIT",
  // Line weight integers
  371: "DIMLWD",
  372: "DIMLWE",
  // String codes (handle references in pure DXF, but we round-trip via
  // plain name strings — AutoCAD tolerates built-in arrow block names)
  3: "DIMPOST",
  4: "DIMAPOST",
  340: "DIMTXSTY",
  341: "DIMLDRBLK",
  342: "DIMBLK",
  343: "DIMBLK1",
  344: "DIMBLK2",
};

const DIMVAR_STRING_CODES = new Set([3, 4, 340, 341, 342, 343, 344]);

/**
 * Extract the table-LEVEL header groups (table handle `5`, owner `330`, subclass
 * `100/AcDbSymbolTable`, max-entries `70`, plus any vendor codes) that appear
 * BETWEEN the `2/<name>` table-name marker and the first `0/<recordMarker>`
 * record. A conformant AutoCAD/ezdxf R2000 table head is
 * `0/TABLE 2/<name> 5/<hex> 330/0 100/AcDbSymbolTable 70/<count>`; the writers
 * for LAYER / STYLE / DIMSTYLE / BLOCK_RECORD previously DROPPED this head (only
 * LTYPE captured it via `linetypeTableHeader`), so a parse→serialize round-trip
 * emitted a head-less table that ezdxf `audit()` SILENTLY repaired ("Fixed
 * invalid table head handle in table …") — i.e. AutoCAD would invoke recovery.
 *
 * Captures the head verbatim so the writer re-emits the REAL `5/<handle>`
 * instead of synthesising one. Excludes the `0/TABLE` + `2/<name>` markers (the
 * writer re-emits those itself), the per-record groups and the `0/ENDTAB`
 * terminator. Returns `undefined` when there is no head (no group before the
 * first record) so the caller omits the field, matching the LTYPE precedent.
 *
 * `table.groups` is the per-table slice built by `parseTablesSection` and starts
 * with the `0/TABLE` + `2/<name>` markers it pushed (see the splitter loop).
 */
function extractSymbolTableHead(
  tableGroups: DxfGroup[],
  recordMarker: string,
): DxfGroup[] | undefined {
  const head: DxfGroup[] = [];
  let sawTableName = false;
  for (const g of tableGroups) {
    if (g.code === 0 && g.value === "TABLE") continue; // table marker
    if (!sawTableName && g.code === 2) {
      sawTableName = true; // table-name marker — writer re-emits it
      continue;
    }
    if (g.code === 0 && g.value === "ENDTAB") break; // table terminator
    if (g.code === 0 && g.value === recordMarker) break; // first record reached
    head.push(g);
  }
  return head.length > 0 ? head : undefined;
}

export function parseTablesSection(groups: DxfGroup[]): DxfTablesSection {
  const textStyles: DxfTextStyleEntry[] = [];
  const dimensionStyles: DxfDimensionStyleEntry[] = [];
  const layers: DxfLayerEntry[] = [];
  const blockRecords: DxfBlockRecordEntry[] = [];
  const linetypes: DxfLinetypeEntry[] = [];
  let linetypeTableHeader: DxfGroup[] | undefined;
  // FIX-110 — capture the LAYER / STYLE / DIMSTYLE / BLOCK_RECORD table-LEVEL
  // heads (`5/<hex> 330/0 100/AcDbSymbolTable 70/<count>`) so the writer
  // re-emits the real head instead of dropping it (ezdxf audit would otherwise
  // SILENTLY repair the head-less table → AutoCAD recovery). Mirrors the
  // existing `linetypeTableHeader` mechanism, generalised to the other four
  // typed symbol tables. Keyed by table name in `symbolTableHeaders`.
  const symbolTableHeaders: Record<string, DxfGroup[]> = {};
  const rawTableGroups: DxfGroup[] = [];

  // Split the section groups into individual tables by TABLE/ENDTAB boundaries
  type TableBlock = { tableName: string; groups: DxfGroup[] };
  const tables: TableBlock[] = [];
  let currentTable: TableBlock | null = null;

  for (let i = 0; i < groups.length; i++) {
    const g = groups[i]!;
    if (g.code === 0 && g.value === "TABLE") {
      // Next group should be code 2 with the table name
      const nameGroup = groups[i + 1];
      const tableName =
        nameGroup && nameGroup.code === 2 ? nameGroup.value.trim() : "";
      currentTable = { tableName, groups: [] };
      // Push the TABLE marker and name group into the table's groups for raw passthrough
      currentTable.groups.push(g);
      if (nameGroup && nameGroup.code === 2) {
        currentTable.groups.push(nameGroup);
        i++; // skip the name group, already consumed
      }
      continue;
    }
    if (g.code === 0 && g.value === "ENDTAB") {
      if (currentTable) {
        currentTable.groups.push(g);
        tables.push(currentTable);
        currentTable = null;
      }
      continue;
    }
    if (currentTable) {
      currentTable.groups.push(g);
    }
  }

  for (const table of tables) {
    if (table.tableName === "STYLE") {
      // Parse STYLE table entries
      // Split entries by code 0 "STYLE"
      const head = extractSymbolTableHead(table.groups, "STYLE");
      if (head) symbolTableHeaders.STYLE = head;
      let entryGroups: DxfGroup[] | null = null;
      for (const g of table.groups) {
        if (g.code === 0 && g.value === "STYLE") {
          if (entryGroups) {
            textStyles.push(parseTextStyleEntry(entryGroups));
          }
          entryGroups = [];
          continue;
        }
        if (entryGroups) {
          entryGroups.push(g);
        }
      }
      if (entryGroups) {
        textStyles.push(parseTextStyleEntry(entryGroups));
      }
    } else if (table.tableName === "DIMSTYLE") {
      // Parse DIMSTYLE table entries
      const head = extractSymbolTableHead(table.groups, "DIMSTYLE");
      if (head) symbolTableHeaders.DIMSTYLE = head;
      let entryGroups: DxfGroup[] | null = null;
      for (const g of table.groups) {
        if (g.code === 0 && g.value === "DIMSTYLE") {
          if (entryGroups) {
            dimensionStyles.push(parseDimStyleEntry(entryGroups));
          }
          entryGroups = [];
          continue;
        }
        if (entryGroups) {
          entryGroups.push(g);
        }
      }
      if (entryGroups) {
        dimensionStyles.push(parseDimStyleEntry(entryGroups));
      }
    } else if (table.tableName === "LAYER") {
      // Parse LAYER table entries — structured parsing for layer properties
      const head = extractSymbolTableHead(table.groups, "LAYER");
      if (head) symbolTableHeaders.LAYER = head;
      let entryGroups: DxfGroup[] | null = null;
      for (const g of table.groups) {
        if (g.code === 0 && g.value === "LAYER") {
          if (entryGroups) {
            layers.push(parseLayerEntry(entryGroups));
          }
          entryGroups = [];
          continue;
        }
        if (entryGroups) {
          entryGroups.push(g);
        }
      }
      if (entryGroups) {
        layers.push(parseLayerEntry(entryGroups));
      }
    } else if (table.tableName === "BLOCK_RECORD") {
      // Parse BLOCK_RECORD table entries. Every BLOCK in the BLOCKS
      // section is mirrored here by a BLOCK_RECORD that stores block
      // metadata (insertion units, explodability, layout handle,
      // annotative flag). AutoCAD ALWAYS provides `*Model_Space` and
      // `*Paper_Space` entries — we preserve them verbatim.
      const head = extractSymbolTableHead(table.groups, "BLOCK_RECORD");
      if (head) symbolTableHeaders.BLOCK_RECORD = head;
      let entryGroups: DxfGroup[] | null = null;
      for (const g of table.groups) {
        if (g.code === 0 && g.value === "BLOCK_RECORD") {
          if (entryGroups) {
            blockRecords.push(parseBlockRecordEntry(entryGroups));
          }
          entryGroups = [];
          continue;
        }
        if (entryGroups) {
          entryGroups.push(g);
        }
      }
      if (entryGroups) {
        blockRecords.push(parseBlockRecordEntry(entryGroups));
      }
    } else if (table.tableName === "LTYPE") {
      // Parse LTYPE table entries into typed records (PR-LT2). Promoted out
      // of the `rawTableGroups` "all other tables" branch so a custom /
      // complex linetype is programmatically accessible AND the linetype
      // refcheck (plan §10.5) can resolve real names. Because LTYPE no longer
      // lands in `rawTableGroups`, the writer serialises it exactly once from
      // this typed array — no duplication (plan §10.3 / Codex#1 F10).
      //
      // Table-LEVEL header groups (table handle `5`, owner `330`, subclass
      // `100/AcDbSymbolTable`, max-entries `70`, …) live BETWEEN `2/LTYPE`
      // and the first `0/LTYPE`. Capture them (Codex#2 P2) so the writer
      // re-emits the real header instead of synthesising a minimal one. The
      // `0/TABLE` + `2/LTYPE` markers (re-emitted by the writer) are skipped.
      const header: DxfGroup[] = [];
      let sawTableName = false;
      let entryGroups: DxfGroup[] | null = null;
      for (const g of table.groups) {
        if (g.code === 0 && g.value === "TABLE") continue; // table marker
        if (!sawTableName && g.code === 2 && g.value.trim() === "LTYPE") {
          sawTableName = true; // table-name marker — writer re-emits it
          continue;
        }
        if (g.code === 0 && g.value === "ENDTAB") continue; // table terminator
        if (g.code === 0 && g.value === "LTYPE") {
          if (entryGroups) {
            linetypes.push(parseLinetypeEntry(entryGroups));
          }
          entryGroups = [];
          continue;
        }
        if (entryGroups) {
          entryGroups.push(g);
        } else {
          // Before the first `0/LTYPE` record → table-level header group.
          header.push(g);
        }
      }
      if (entryGroups) {
        linetypes.push(parseLinetypeEntry(entryGroups));
      }
      if (header.length > 0) linetypeTableHeader = header;
    } else {
      // All other tables (VPORT / VIEW / UCS / APPID / …) → rawTableGroups
      // (preserve verbatim). NOTE: use for-loop instead of spread for the
      // same reason as in the OBJECTS section — pathological DXF files with
      // huge custom TABLE entries would overflow V8's argument-count limit on
      // `push(...huge)`.
      for (const g of table.groups) rawTableGroups.push(g);
    }
  }

  return {
    kind: "tables-section",
    textStyles,
    dimensionStyles,
    layers,
    blockRecords,
    ...(linetypes.length > 0 ? { linetypes } : {}),
    ...(linetypeTableHeader ? { linetypeTableHeader } : {}),
    ...(Object.keys(symbolTableHeaders).length > 0
      ? { symbolTableHeaders }
      : {}),
    rawTableGroups,
  };
}

// Known LTYPE table-record group codes. Anything outside this set (and not
// the table marker / class subclass codes already consumed) goes to
// `extraGroups` so the writer can round-trip vendor extensions. The
// per-element codes (49 / 74 / 75 / 340 / 46 / 50 / 44 / 45 / 9) are handled
// by the element accumulator, NOT this set.
const LTYPE_KNOWN_CODES = new Set<number>([
  0,
  100, // entity / subclass markers
  2,
  5,
  330, // name, handle, owner
  3,
  70,
  72,
  73,
  40, // description, flags, alignment, count, length
]);

const LTYPE_ELEMENT_CODES = new Set<number>([
  49, 74, 75, 340, 46, 50, 44, 45, 9,
]);

/**
 * Parse a single LTYPE table record into a typed `DxfLinetypeEntry`.
 *
 * Element accumulation: a new element opens on group 49 (the signed
 * dash/gap/dot length); the complex codes that AutoCAD emits AFTER it
 * (74 type, 75 shape number, 340 STYLE handle, 46 scale, 50 rotation,
 * 44/45 offsets, 9 text) attach to the currently-open element. `40`
 * (total pattern length) and `73` (element count) are kept RAW so a
 * plain round-trip re-emits them verbatim even if inconsistent.
 *
 * FIX-B (Codex#2 PR-LT2 P2 — nested `102` reactor block): a record may carry a
 * nested reactor block `102/{ACAD_REACTORS ... 330/<handle> ... 102/}` (and
 * likewise a `102/{ACAD_XDICTIONARY 360/<handle> 102/}`) between its handle (5)
 * and the record owner (330). We track `102` nesting depth exactly like
 * `parseBlockRecordEntry`: a `102/{...` OPENS a nested block, a `102/}` CLOSES
 * it. Only a TOP-LEVEL `330` (outside any open `102` block) is the record
 * `ownerHandle`; every group INSIDE an open `102` block — the `102/{` open
 * marker, the inner `330`/`360` handle, and the `102/}` close marker — is
 * preserved VERBATIM in `extraGroups` in original order so the writer re-emits
 * the nested block byte-for-byte. Previously EVERY `330` was treated as the
 * owner (the reactor's inner `330` overwrote the real owner and was dropped),
 * while the `102` markers were preserved but de-nested. Pinned by
 * `linetypeTable.test.ts` → "LTYPE nested reactor block".
 */
export function parseLinetypeEntry(groups: DxfGroup[]): DxfLinetypeEntry {
  const entry: DxfLinetypeEntry = { name: "", elements: [] };
  const extraGroups: DxfGroup[] = [];
  let current: DxfLinetypeElement | null = null;
  // FIX-B (Codex#2 PR-LT2 P2) — record whether THIS record carried the
  // `100/AcDbLinetypeTableRecord` subclass marker. R12 / minimal DXFs omit it;
  // synthesising one on export breaks byte parity for those files. The writer
  // emits `100` only when the source had it (or for a synthesised entry that
  // never set the flag — defaults emit, conservative).
  let hadSubclassMarker = false;
  // FIX-B — `102` nested-block depth (reactor / xdictionary). Mirrors
  // `parseBlockRecordEntry`'s bookkeeping: `102/{...` opens a block, `102/}`
  // closes it. While depth > 0 EVERY group (including the `102` boundary
  // markers themselves) is preserved verbatim in `extraGroups`, in original
  // order, so the writer re-emits the nested block byte-for-byte. Only a
  // TOP-LEVEL `330` reaches the `case 330` owner-handle assignment below.
  let nestingDepth = 0;

  for (const g of groups) {
    // Nested `102` block boundaries + contents — capture VERBATIM.
    if (g.code === 102) {
      const tok = g.value.trim();
      if (tok.startsWith("{")) {
        nestingDepth++;
        extraGroups.push(g);
        continue;
      }
      if (tok === "}") {
        if (nestingDepth > 0) nestingDepth--;
        extraGroups.push(g);
        continue;
      }
      // Unexpected `102` payload (neither open nor close) — still preserve.
      extraGroups.push(g);
      continue;
    }
    if (nestingDepth > 0) {
      // Inside an open `102` block — the inner `330`/`360` handle (and any
      // other code) is part of the nested structure, NOT a record-level field.
      extraGroups.push(g);
      continue;
    }
    if (
      g.code === 100 &&
      g.value.trim() === "AcDbLinetypeTableRecord"
    ) {
      hadSubclassMarker = true;
      continue;
    }
    if (g.code === 49) {
      // A new pattern element opens. Push the previous one first.
      if (current) entry.elements.push(current);
      current = { length: Number(g.value) };
      continue;
    }
    if (current && LTYPE_ELEMENT_CODES.has(g.code)) {
      switch (g.code) {
        case 74:
          current.elementType = Number.parseInt(g.value, 10) || 0;
          break;
        case 75:
          current.shapeNumber = Number.parseInt(g.value, 10) || 0;
          break;
        case 340:
          current.styleHandle = g.value.trim();
          break;
        case 46:
          current.scale = Number(g.value);
          break;
        case 50:
          current.rotation = Number(g.value);
          break;
        case 44:
          current.offsetX = Number(g.value);
          break;
        case 45:
          current.offsetY = Number(g.value);
          break;
        case 9:
          current.text = decodeDxfUnicode(g.value); // T9 PR-3 — complex-LTYPE element text decode
          break;
      }
      continue;
    }

    switch (g.code) {
      case 2:
        entry.name = decodeDxfUnicode(g.value.trim()); // T9 PR-3 name decode
        break;
      case 5:
        entry.handle = g.value.trim();
        break;
      case 330:
        entry.ownerHandle = g.value.trim();
        break;
      case 3:
        entry.description = decodeDxfUnicode(g.value); // T9 PR-3 description decode
        break;
      case 70:
        entry.flags = Number.parseInt(g.value, 10) || 0;
        break;
      case 72: {
        // FIX-A (Codex#2 PR-LT2 P2) — use NULLISH handling, not a truthiness
        // fallback. A dirty/vendor `72=0` is a real (non-65) alignment that the
        // bridge preserves in `alignmentRaw`; `Number.parseInt(...) || 65` would
        // coerce that `0` to 65 BEFORE the bridge sees it, silently losing it on
        // round-trip. Only a genuinely unparseable value falls back to 65.
        const parsed72 = Number.parseInt(g.value, 10);
        entry.alignment = Number.isNaN(parsed72) ? 65 : parsed72;
        break;
      }
      case 73:
        entry.elementCount = Number.parseInt(g.value, 10) || 0;
        break;
      case 40:
        entry.patternLength = Number(g.value);
        break;
      default:
        if (!LTYPE_KNOWN_CODES.has(g.code)) {
          extraGroups.push(g);
        }
        break;
    }
  }
  if (current) entry.elements.push(current);
  if (extraGroups.length > 0) entry.extraGroups = extraGroups;
  // FIX-B — the parser ALWAYS knows whether the marker was present, so set the
  // flag EXPLICITLY (true | false). The writer distinguishes a parsed-but-absent
  // R12 record (`false` → suppress `100`) from a freshly built / seeded entry
  // (`undefined` → emit, AutoCAD-R13+ default). A bare `if (hadSubclassMarker)`
  // would leave a real R12 record `undefined` and wrongly re-synthesise `100`.
  entry.hadSubclassMarker = hadSubclassMarker;
  return entry;
}

function parseTextStyleEntry(groups: DxfGroup[]): DxfTextStyleEntry {
  const entry: DxfTextStyleEntry = { name: "" };
  // ROOT E — track the `100/AcDbTextStyleTableRecord` subclass marker so the
  // writer re-emits the 100/ groups ONLY for sources that carried them (R13+),
  // keeping marker-less R12 records byte-faithful.
  let hadSubclassMarker = false;
  // T9b — `102 {…}` nesting depth (reactors / xdictionary), same bookkeeping as
  // `parseLinetypeEntry`: only a TOP-LEVEL `330` is the record owner.
  let nestingDepth = 0;
  for (const g of groups) {
    nestingDepth = trackNestedBlockDepth(g, nestingDepth);
    if (g.code === 102 || nestingDepth > 0) continue;
    if (g.code === 100 && g.value.trim() === "AcDbTextStyleTableRecord") {
      hadSubclassMarker = true;
      continue;
    }
    switch (g.code) {
      case 2:
        entry.name = decodeDxfUnicode(g.value.trim()); // T9 PR-3 style name decode
        break;
      case 3:
        entry.fontName = decodeDxfUnicode(g.value.trim()); // T9 PR-3 font filename decode
        break;
      case 4:
        entry.bigFontFile = decodeDxfUnicode(g.value.trim()); // T9 PR-3 bigfont decode
        break;
      case 5:
        entry.handle = g.value.trim();
        break;
      case 330:
        entry.ownerHandle = g.value.trim(); // T9b — STYLE table-head owner
        break;
      case 40:
        entry.height = Number(g.value);
        break;
      case 41:
        entry.widthFactor = Number(g.value);
        break;
      case 42:
        entry.lastHeight = Number(g.value);
        break;
      case 50:
        entry.obliqueAngle = Number(g.value);
        break;
      case 70:
        entry.flags = Number.parseInt(g.value, 10);
        break;
      case 71:
        entry.generationFlags = Number.parseInt(g.value, 10);
        break;
      case 1071:
        entry.fontCharset = Number.parseInt(g.value, 10);
        break;
    }
  }
  // Set EXPLICITLY (true | false) — the writer distinguishes a parsed-but-absent
  // R12 record (`false` → suppress 100/) from a fresh/skeleton entry
  // (`undefined` → emit 100/, the R13+ default).
  entry.hadSubclassMarker = hadSubclassMarker;
  return entry;
}

export function parseLayerEntry(groups: DxfGroup[]): DxfLayerEntry {
  const entry: DxfLayerEntry = {
    name: "",
    color: 7,
    flags: 0,
    linetype: "Continuous",
    lineweight: -3,
    plottable: true,
  };
  // T9b — see parseTextStyleEntry: a `330` inside a `102 {ACAD_REACTORS …}`
  // block is a reactor, not the owner.
  let nestingDepth = 0;
  for (const g of groups) {
    nestingDepth = trackNestedBlockDepth(g, nestingDepth);
    if (g.code === 102 || nestingDepth > 0) continue;
    switch (g.code) {
      case 2:
        entry.name = decodeDxfUnicode(g.value.trim()); // T9 PR-3 layer name decode
        break;
      case 5:
        entry.handle = g.value.trim();
        break;
      case 330:
        entry.ownerHandle = g.value.trim(); // T9b — LAYER table-head owner
        break;
      case 390:
        entry.plotStyleHandle = g.value.trim(); // T9b — PlotStyleName object
        break;
      case 347:
        entry.materialHandle = g.value.trim(); // T9b — MATERIAL object
        break;
      case 6:
        entry.linetype = decodeDxfUnicode(g.value.trim()); // T9 PR-3 linetype ref decode
        break;
      case 62:
        entry.color = Number.parseInt(g.value, 10);
        break;
      case 70:
        entry.flags = Number.parseInt(g.value, 10);
        break;
      case 290:
        entry.plottable = g.value.trim() !== "0";
        break;
      case 370:
        entry.lineweight = Number.parseInt(g.value, 10);
        break;
    }
  }
  return entry;
}

// Known BLOCK_RECORD group codes. Anything outside this set goes to
// `extraGroups` so the writer can round-trip vendor extensions.
const BLOCK_RECORD_KNOWN_CODES = new Set<number>([
  0,
  100, // entity markers
  2,
  5,
  330, // name, handle, owner
  70,
  280,
  281,
  340, // documented BLOCK_RECORD fields
]);

export function parseBlockRecordEntry(groups: DxfGroup[]): DxfBlockRecordEntry {
  const entry: DxfBlockRecordEntry = { name: "" };
  const extraGroups: DxfGroup[] = [];
  const xdata: DxfXDataEntry[] = [];
  let currentXData: DxfXDataEntry | null = null;
  // Extension-dictionary / reactor nested group blocks. AutoCAD formats
  // these as a balanced sequence:
  //   102 {ACAD_XDICTIONARY   ← open
  //   360 <handle>
  //   102 }                   ← close
  //   102 {ACAD_REACTORS      ← open
  //   330 <handle> [330 …]
  //   102 }                   ← close
  // On a dynamic block the XDICTIONARY handle points at a DICTIONARY in
  // the OBJECTS section that owns ACAD_ENHANCEDBLOCK (the eval-graph
  // record) — so capturing the handle here is the reliable marker.
  type NestedKind = "xdict" | "reactors" | "other";
  let nested: NestedKind | null = null;
  const reactorHandles: string[] = [];

  for (const g of groups) {
    // XData accumulation — AcDbBlockRepresentation stores the annotative
    // flag. Any 1001 starts a new XData block; 1000-1071 codes belong to
    // the currently open XData; anything else closes the block.
    if (g.code === 1001) {
      if (currentXData) xdata.push(currentXData);
      // T9 PR-3 — 1001 appid is in the write allowlist (escaped); decode here so
      // a non-ASCII appid round-trips (no-op for ASCII registered app names).
      currentXData = { appName: decodeDxfUnicode(g.value).trim(), groups: [] };
      continue;
    }
    if (currentXData && g.code >= 1000 && g.code <= 1071) {
      // T9 PR-3 — decode XDATA 1000/1003 string values at the COLLECTOR (no-op
      // for non-string / ASCII codes); re-export re-escapes → byte-identical.
      currentXData.groups.push(decodeXdataGroupValue(g));
      continue;
    }
    if (currentXData) {
      xdata.push(currentXData);
      currentXData = null;
    }

    // Nested 102 blocks — open/close pair captured separately so we can
    // route handles inside them (360 for XDICTIONARY, 330 for reactors).
    if (g.code === 102) {
      const tok = g.value.trim();
      if (tok.startsWith("{")) {
        const inner = tok.slice(1).trim().toUpperCase();
        if (inner === "ACAD_XDICTIONARY") nested = "xdict";
        else if (inner === "ACAD_REACTORS") nested = "reactors";
        else nested = "other";
        continue;
      }
      if (tok === "}") {
        nested = null;
        continue;
      }
      // Unexpected 102 payload — preserve through extraGroups.
      if (!BLOCK_RECORD_KNOWN_CODES.has(g.code)) extraGroups.push(g);
      continue;
    }
    if (nested === "xdict") {
      if (g.code === 360 && entry.extensionDictionaryHandle === undefined) {
        entry.extensionDictionaryHandle = g.value.trim();
      }
      // Don't push into extraGroups — the handle is already decoded; any
      // future writer will reconstruct the 102 pair from the typed field.
      continue;
    }
    if (nested === "reactors") {
      if (g.code === 330) {
        reactorHandles.push(g.value.trim());
      }
      continue;
    }

    switch (g.code) {
      case 2:
        entry.name = decodeDxfUnicode(g.value.trim()); // T9 PR-3 blockrecord name decode
        break;
      case 5:
        entry.handle = g.value.trim();
        break;
      case 330:
        entry.ownerHandle = g.value.trim();
        break;
      case 70:
        entry.insertionUnits = Number.parseInt(g.value, 10) || 0;
        break;
      case 280:
        entry.explodability = Number.parseInt(g.value, 10) || 0;
        break;
      case 281:
        entry.scalability = Number.parseInt(g.value, 10) || 0;
        break;
      case 340:
        entry.layoutHandle = g.value.trim();
        break;
      default:
        if (!BLOCK_RECORD_KNOWN_CODES.has(g.code)) {
          extraGroups.push(g);
        }
        break;
    }
  }
  if (currentXData) xdata.push(currentXData);
  if (reactorHandles.length > 0) entry.reactorHandles = reactorHandles;

  // Decode the annotative flag if present. AutoCAD stores it under the
  // ACAD app name as (1000 "AcDbBlockRepresentation", 1070 1001, 1070 N).
  // N is a bitfield; bit 0 = annotative.
  for (const xd of xdata) {
    if (xd.appName === "ACAD" || xd.appName === "AcDbBlockRepresentation") {
      let sawAnnotativeKey = false;
      for (const g of xd.groups) {
        if (g.code === 1000 && g.value === "AcDbBlockRepresentation") {
          sawAnnotativeKey = true;
          continue;
        }
        if (sawAnnotativeKey && g.code === 1070) {
          const n = Number.parseInt(g.value, 10) || 0;
          // Following AutoCAD convention, two 1070 records are emitted —
          // 1001 (marker) and the annotative flag. We treat any 1070
          // with value 1 following the marker as annotative = true.
          if (n === 1) entry.isAnnotative = true;
        }
      }
    }
  }

  // ── AcadAnnotativeDecomposition (PR-2) ──
  // For annotative blocks, AutoCAD persists a `scaleName → *A_NNN`
  // mapping under a dedicated XData app-name. Decode it into a typed
  // `Record<string, string>` so the bridge layer can populate
  // `BlockRecord.annotativeDecompositions` without reparsing groups.
  // Empty XData entries are silently ignored — their absence is
  // semantically equivalent to "no decomposition materialised yet".
  for (const xd of xdata) {
    if (xd.appName !== "AcadAnnotativeDecomposition") continue;
    const decompositions = parseAcadAnnotativeDecomposition(xd.groups);
    if (Object.keys(decompositions).length > 0) {
      entry.annotativeDecompositions = decompositions;
    }
  }

  if (xdata.length > 0) entry.xdata = xdata;
  if (extraGroups.length > 0) entry.extraGroups = extraGroups;
  return entry;
}

export function parseDimStyleEntry(groups: DxfGroup[]): DxfDimensionStyleEntry {
  const entry: DxfDimensionStyleEntry = { name: "", dimVars: {} };
  // ROOT E — track the `100/AcDbDimStyleTableRecord` subclass marker (see
  // parseTextStyleEntry).
  let hadSubclassMarker = false;
  // T9b — see parseTextStyleEntry: only a TOP-LEVEL `330` is the owner.
  let nestingDepth = 0;
  for (const g of groups) {
    nestingDepth = trackNestedBlockDepth(g, nestingDepth);
    if (g.code === 102 || nestingDepth > 0) continue;
    if (g.code === 100 && g.value.trim() === "AcDbDimStyleTableRecord") {
      hadSubclassMarker = true;
      continue;
    }
    if (g.code === 2) {
      entry.name = decodeDxfUnicode(g.value.trim()); // T9 PR-3 dimstyle name decode
      continue;
    }
    if (g.code === 330) {
      entry.ownerHandle = g.value.trim(); // T9b — DIMSTYLE table-head owner
      continue;
    }
    // SL-4 — the DIMSTYLE record's own handle is group code 105 (not 5). Code 5
    // in a DIMSTYLE record is the obsolete DIMBLK object-id per the Autodesk
    // R2018 reference; the record handle moved to 105 (ezdxf
    // `_handle_code = 105 if dxftype=="DIMSTYLE"`). Read 105 as the canonical
    // R13+ code; keep a lenient 5 fallback so pre-SL-4 output and legacy R12
    // DIMSTYLE records still round-trip. 105 wins if BOTH appear (the guarded 5
    // branch never clobbers a handle already taken from 105).
    if (g.code === 105) {
      entry.handle = g.value.trim();
      continue;
    }
    if (g.code === 5 && entry.handle === undefined) {
      entry.handle = g.value.trim();
      continue;
    }
    // Group 70 is the DIMSTYLE flag byte — bit 8 (value 16) is the
    // annotative-table-entry flag. Decompose it into the normalized
    // DIMANNO dimvar so downstream code can reason about annotative
    // state without re-parsing bit flags. Other bits (xref-dependent,
    // xref-resolved) flow through unchanged.
    if (g.code === 70) {
      const flagByte = Number(g.value);
      if (Number.isFinite(flagByte) && (flagByte & 16) !== 0) {
        entry.dimVars.DIMANNO = 1;
      }
      continue;
    }
    const dimVarName = DIMVAR_CODE_MAP[g.code];
    if (dimVarName) {
      if (DIMVAR_STRING_CODES.has(g.code)) {
        // T9 PR-3 — codes 3/4 (DIMPOST/DIMAPOST) carry free-form text and codes
        // 340-344 (DIMTXSTY/DIMLDRBLK/DIMBLK/DIMBLK1/DIMBLK2) carry text-style /
        // arrow-block NAMES in this repo's DIMSTYLE round-trip (the writer emits
        // the plain name, not a handle, and `\U+`-escapes it now that 340-344 are
        // in STRING_CONTENT_GROUP_CODES). Decode here so a Cyrillic name re-imports
        // as the real characters. On a genuine hex handle the decode is a no-op
        // (no `\U+` in hex), so handle-valued DIMSTYLE files are unaffected.
        entry.dimVars[dimVarName] = decodeDxfUnicode(g.value.trim());
      } else {
        entry.dimVars[dimVarName] = Number(g.value);
      }
    }
  }
  entry.hadSubclassMarker = hadSubclassMarker;
  return entry;
}

/* ── OBJECTS section parsing ──────────────────────── */

// Known MLEADERSTYLE group codes — see AutoCAD DXF Reference 2018+,
// ezdxf MultiLeader documentation, and atlight.github.io DXF notes.
const MLEADERSTYLE_KNOWN_CODES = new Set<number>([
  0, 2, 3, 5, 100, 102, 330, 360,
  // Numeric flags / integers.
  // SL-5 F2/F4 (@codex round-4): 171 (draw_mleader_order_type) is DELIBERATELY
  // absent — it has no typed field, so leaving it out of this set routes it to
  // extraGroups (verbatim round-trip) instead of the silent-drop of a "known but
  // unhandled" code. 178 (text_right_attachment_type) is now typed (case 178).
  40, 41, 42, 43, 44, 45, 46, 47, 49, 140, 141, 142, 143, 90, 91, 92, 93, 94,
  170, 172, 173, 174, 175, 176, 177, 178, 270, 271, 272, 273, 274, 275, 276,
  277, 278, 279, 290, 291, 292, 293, 294, 296, 297,
  // #432 r9 E3 — 298 (ezdxf `unknown2`, optional) has NO parser case and NO
  // writer emit, so listing it here as "known" SILENTLY DROPPED it. Removed from
  // the set → it falls to the default passthrough and round-trips verbatim via
  // extraGroups (we do not model it). (274 stays: not an ezdxf acdb_mleader_style
  // attribute, so a conformant file never carries it.)
  // Handle references
  340, 341, 342, 343,
  // String
  300,
]);

// Known DICTIONARY group codes that have a typed home below. Anything
// outside this set (and outside the nested-102 / XData machinery) is
// preserved verbatim through `extraGroups` so the writer can re-emit it
// (Batch-6 S-OBJECTS-DICT-ROUNDTRIP — mirror BLOCK_RECORD).
const DICTIONARY_KNOWN_CODES = new Set<number>([
  0,
  100, // entity markers (AcDbDictionary / AcDbDictionaryWithDefault)
  5,
  330, // handle, owner (330 outside reactor block)
  280,
  281, // hard-owned, cloning flags
  3,
  350,
  360, // child name + soft/hard pointers
  340, // default-entry handle (ACDBDICTIONARYWDFLT)
]);

export function parseDictionaryEntry(
  groups: DxfGroup[],
  objectType?: string,
): DxfDictionaryEntry {
  const entry: DxfDictionaryEntry = {
    kind: "dictionary",
    handle: "",
    hardOwned: false,
    cloning: 1,
    entries: [],
  };
  if (objectType && objectType.trim().toUpperCase() === "ACDBDICTIONARYWDFLT") {
    entry.isWithDefault = true;
  }

  const extraGroups: DxfGroup[] = [];
  const xdata: DxfXDataEntry[] = [];
  let currentXData: DxfXDataEntry | null = null;
  // Extension-dictionary / reactor nested 102 blocks (mirror BLOCK_RECORD):
  //   102 {ACAD_XDICTIONARY → 360 <handle> → 102 }
  //   102 {ACAD_REACTORS    → 330 <handle> → 102 }
  type NestedKind = "xdict" | "reactors" | "other";
  let nested: NestedKind | null = null;
  const reactorHandles: string[] = [];

  // Dictionary records reference their child objects through pairs of
  // (code 3 → entry name, code 350/360 → handle). We walk the groups
  // sequentially so we can associate each name with the following
  // pointer code regardless of order variation between CAD vendors.
  let pendingName: string | null = null;
  for (const g of groups) {
    // XData accumulation — any 1001 starts a new XData block; 1000-1071
    // codes belong to the currently open block; anything else closes it.
    if (g.code === 1001) {
      if (currentXData) xdata.push(currentXData);
      // T9 PR-3 — 1001 appid is in the write allowlist (escaped); decode here so
      // a non-ASCII appid round-trips (no-op for ASCII registered app names).
      currentXData = { appName: decodeDxfUnicode(g.value).trim(), groups: [] };
      continue;
    }
    if (currentXData && g.code >= 1000 && g.code <= 1071) {
      // T9 PR-3 — decode XDATA 1000/1003 string values at the COLLECTOR (no-op
      // for non-string / ASCII codes); re-export re-escapes → byte-identical.
      currentXData.groups.push(decodeXdataGroupValue(g));
      continue;
    }
    if (currentXData) {
      xdata.push(currentXData);
      currentXData = null;
    }

    // Nested 102 open/close pairs — route handles inside them.
    if (g.code === 102) {
      const tok = g.value.trim();
      if (tok.startsWith("{")) {
        const inner = tok.slice(1).trim().toUpperCase();
        if (inner === "ACAD_XDICTIONARY") nested = "xdict";
        else if (inner === "ACAD_REACTORS") nested = "reactors";
        else {
          nested = "other";
          // Unknown (vendor) 102 control block: the ACAD_XDICTIONARY /
          // ACAD_REACTORS blocks are re-synthesised by the writer from typed
          // fields, but an unknown block has no typed home — preserve the
          // OPENING `102/{NAME}` delimiter verbatim so the block round-trips
          // as an opaque scoped unit and its inner 330/360 handles stay
          // SCOPED (never re-promoted to top-level owner/child refs on the
          // next import) (Codex#248 F2 P2).
          extraGroups.push(g);
        }
        continue;
      }
      if (tok === "}") {
        // Closing delimiter — only the unknown-block closer must survive into
        // extraGroups (xdict/reactors closers are re-synthesised by the
        // writer). Keeping it with the payload reproduces the open/payload/
        // close sequence and preserves the inner handles' control-block
        // scoping (Codex#248 F2 P2).
        if (nested === "other") extraGroups.push(g);
        nested = null;
        continue;
      }
      if (!DICTIONARY_KNOWN_CODES.has(g.code)) extraGroups.push(g);
      continue;
    }
    if (nested === "xdict") {
      if (g.code === 360 && entry.extensionDictionaryHandle === undefined) {
        entry.extensionDictionaryHandle = g.value.trim();
      }
      continue;
    }
    if (nested === "reactors") {
      if (g.code === 330) reactorHandles.push(g.value.trim());
      continue;
    }
    if (nested === "other") {
      // Unknown nested 102 block payload — preserve verbatim.
      extraGroups.push(g);
      continue;
    }

    switch (g.code) {
      case 5:
        entry.handle = g.value.trim();
        break;
      case 330:
        entry.ownerHandle = g.value.trim();
        break;
      case 280:
        entry.hardOwned = Number.parseInt(g.value, 10) !== 0;
        break;
      case 281:
        entry.cloning = Number.parseInt(g.value, 10);
        break;
      case 340:
        // Default-entry handle on ACDBDICTIONARYWDFLT records.
        entry.defaultHandle = g.value.trim();
        break;
      case 3:
        // T9 PR-3 — DICTIONARY entry name is `\U+`-escaped on write (the writer
        // emits the typed `entry.entries[].name` via the code-3 chokepoint);
        // decode symmetrically so Cyrillic dictionary entry names round-trip.
        pendingName = decodeDxfUnicode(g.value.trim());
        break;
      case 350: // soft-pointer
      case 360: // hard-pointer
        if (pendingName !== null) {
          entry.entries.push({
            name: pendingName,
            handle: g.value.trim(),
            isHard: g.code === 360,
          });
          pendingName = null;
        }
        break;
      case 100:
        // Subclass markers (AcDbDictionary / AcDbDictionaryWithDefault) are
        // re-synthesised by the writer from `isWithDefault`; don't push to
        // extraGroups (would double-emit).
        break;
      default:
        if (!DICTIONARY_KNOWN_CODES.has(g.code)) extraGroups.push(g);
        break;
    }
  }
  if (currentXData) xdata.push(currentXData);
  if (reactorHandles.length > 0) entry.reactorHandles = reactorHandles;
  if (xdata.length > 0) entry.xdata = xdata;
  if (extraGroups.length > 0) entry.extraGroups = extraGroups;
  return entry;
}

export function parseMultileaderStyleEntry(
  groups: DxfGroup[],
): DxfMultileaderStyleEntry {
  const entry: DxfMultileaderStyleEntry = {
    kind: "mleader-style",
    handle: "",
    name: "",
    extraGroups: {},
  };
  // SL-5 F1 (@codex round-7) — leader_type comes ONLY from a real group 173
  // (ezdxf 1.4.3 `acdb_mleader_style`); 171 (draw_mleader_order_type) is ALWAYS
  // preserved verbatim as draw-order in its own case. Round-6 promoted a no-173
  // 171 to leaderType, but 171 shares the SAME 0-2 value space, so the promotion
  // misread a MODERN draw-order (e.g. `171 2`) as a spline leaderType AND dropped
  // the original 171. A style with NO 173 defaults leader_type to 1 (straight —
  // the ezdxf/AutoCAD default; mirrors model.ts `leaderType: z.number().default(1)`).
  // The residual legacy ambiguity (a pre-round-5 Edit-DWG file that stored a
  // non-straight leader_type at 171 with no 173 reimports as straight) is an
  // unresolvable value-space collision — documented in
  // docs/test-gaps/mleaderstyle-legacy-171-leadertype.md.
  let sawLeaderType = false;

  for (const g of groups) {
    switch (g.code) {
      case 5:
        entry.handle = g.value.trim();
        break;
      case 330:
        entry.ownerHandle = g.value.trim();
        break;
      case 2:
        entry.name = decodeDxfUnicode(g.value.trim()); // T9 PR-3 mleaderstyle name decode
        break;
      case 3:
        entry.description = decodeDxfUnicode(g.value); // T9 PR-3 mleaderstyle description decode
        break;
      case 170:
        entry.contentType = Number.parseInt(g.value, 10);
        break;
      // SL-5 F1 (@codex round-7) — 171 = draw_mleader_order_type (ezdxf
      // `acdb_mleader_style`), NOT leader_type. It has no typed field of its own,
      // so it is ALWAYS round-tripped verbatim via extraGroups — order-independent
      // (the sibling 173 sets leader_type on its own case). Round-6 tried to
      // promote a no-173 171 to leaderType; reverted (its 0-2 value space is
      // indistinguishable from a modern draw-order, so the promotion corrupted
      // both the leaderType and the dropped 171).
      case 171:
        entry.extraGroups["171"] = g.value;
        break;
      case 172:
        entry.textLeftAttachmentTypeDeprecated = Number.parseInt(g.value, 10);
        break;
      // SL-5 F2/F4 — 173 = leader_type ({0=invisible,1=straight,2=spline}),
      // verified against ezdxf 1.4.3. (Was mislabeled as textRightAttachmentType.)
      case 173:
        entry.leaderType = Number.parseInt(g.value, 10);
        sawLeaderType = true; // SL-5 F2 (round-6) — a real 173 makes 171 draw-order
        break;
      // SL-5 F2/F4 — 178 = text_right_attachment_type (ezdxf). Moved off 173 to
      // resolve the leader_type collision. Newly typed (was preserved verbatim).
      case 178:
        entry.textRightAttachmentType = Number.parseInt(g.value, 10);
        break;
      case 174:
        entry.textLeftAttachmentType = Number.parseInt(g.value, 10);
        break;
      case 175:
        entry.textAngleType = Number.parseInt(g.value, 10);
        break;
      case 176:
        entry.textAlignmentType = Number.parseInt(g.value, 10);
        break;
      case 177:
        entry.blockContentConnectionType = Number.parseInt(g.value, 10);
        break;
      case 271:
        entry.textAttachmentDirection = Number.parseInt(g.value, 10);
        break;
      case 272:
        entry.bottomTextAttachmentDirection = Number.parseInt(g.value, 10);
        entry.textBottomAttachmentType = Number.parseInt(g.value, 10);
        break;
      case 273:
        entry.topTextAttachmentDirection = Number.parseInt(g.value, 10);
        entry.textTopAttachmentType = Number.parseInt(g.value, 10);
        break;
      case 274:
        entry.textAttachmentTypeBottom = Number.parseInt(g.value, 10);
        break;
      case 90:
        entry.maxLeaderSegmentPointsNumber = Number.parseInt(g.value, 10);
        break;
      case 91:
        entry.leaderLineColor = g.value.trim();
        break;
      case 92:
        entry.leaderLineWeight = g.value.trim();
        break;
      case 93:
        entry.textColor = g.value.trim();
        break;
      case 94:
        entry.blockContentColor = g.value.trim();
        break;
      case 40:
        entry.firstSegmentAngleConstraint = Number(g.value);
        break;
      case 41:
        entry.secondSegmentAngleConstraint = Number(g.value);
        break;
      case 42:
        // 42 = landing_gap_size (ezdxf acdb_mleader_style), NOT dogleg length.
        entry.landingDistance = Number(g.value);
        break;
      case 43:
        // SL-5 P3b — 43 = dogleg_length (ezdxf acdb_mleader_style). Was in
        // MLEADERSTYLE_KNOWN_CODES but had NO case → silently dropped (a
        // non-default dogleg re-imported as the default 8). Now captured typed.
        entry.doglegLength = Number(g.value);
        break;
      case 44:
        entry.arrowheadSize = Number(g.value);
        break;
      case 45:
        entry.textHeight = Number(g.value);
        break;
      case 46:
        entry.alignSpace = Number(g.value);
        break;
      case 47:
        entry.blockContentScaleX = Number(g.value);
        break;
      case 49:
        entry.blockContentScaleY = Number(g.value);
        break;
      case 140:
        entry.blockContentScaleZ = Number(g.value);
        break;
      case 141:
        entry.blockContentRotation = Number(g.value);
        break;
      case 142:
        entry.scaleOverall = Number(g.value);
        break;
      case 143:
        entry.breakGapSize = Number(g.value);
        break;
      case 290:
        entry.enableLanding = Number.parseInt(g.value, 10) !== 0;
        break;
      case 291:
        entry.enableDogleg = Number.parseInt(g.value, 10) !== 0;
        break;
      case 292:
        entry.textFrameEnabled = Number.parseInt(g.value, 10) !== 0;
        break;
      case 293:
        entry.enableBlockContentScale = Number.parseInt(g.value, 10) !== 0;
        break;
      case 294:
        entry.enableBlockContentRotation = Number.parseInt(g.value, 10) !== 0;
        break;
      case 296:
        entry.enableAnnotationScale = Number.parseInt(g.value, 10) !== 0;
        break;
      case 297:
        entry.textAlignAlwaysLeft = Number.parseInt(g.value, 10) !== 0;
        break;
      case 300:
        entry.textDefault = decodeDxfUnicode(g.value); // T9 PR-3 — MLEADERSTYLE default text decode
        break;
      // SL-5 F2/F4 (@codex round-4) — CORRECTED ref-handle codes per ezdxf 1.4.3
      // `acdb_mleader_style` (empirically verified via `new()` output):
      //   340 = leader_linetype_handle · 341 = arrow_head_handle
      //   342 = text_style_handle      · 343 = block_record_handle
      // Exact inverse of the writer's corrected map. The prior map was inverted
      // (340→textStyle / 341→leaderLinetype / 342→arrowhead) — self-consistent
      // for round-trip but wrong once name-resolved styles emit real refs.
      case 340:
        entry.leaderLineTypeHandle = g.value.trim();
        break;
      case 341:
        entry.arrowheadBlockHandle = g.value.trim();
        break;
      case 342:
        entry.textStyleHandle = g.value.trim();
        break;
      case 343:
        entry.blockContentHandle = g.value.trim();
        break;
      default: {
        // Unknown / vendor-specific group codes are round-tripped verbatim.
        // Skip marker groups that are just structural metadata — code 0 is
        // the type header, code 100 is the subclass marker.
        if (
          g.code !== 0 &&
          g.code !== 100 &&
          !MLEADERSTYLE_KNOWN_CODES.has(g.code)
        ) {
          entry.extraGroups[String(g.code)] = g.value;
        }
        break;
      }
    }
  }

  // #432 r11 (@codex) — do NOT invent a default `leaderType` when the source
  // OMITTED group 173. The round-7 default (`= 1`) was indistinguishable from an
  // explicit `173 1`, so the writer's `entry.leaderType !== undefined` emit ADDED a
  // `173 1` on a source that never carried it (byte-faithfulness violation). Leave
  // it undefined so a pure dxf-io parse→serialize round-trips the OMISSION; the
  // DrawingModel layer supplies the ezdxf/AutoCAD default 1 (bridge import
  // `dxfStyle.leaderType ?? 1` + `model.ts` `leaderType.default(1)`), and an
  // AUTHORED / model-rebuilt MLEADERSTYLE still emits `173` from that model value.
  // `171` stays draw-order in its own case (never promoted — leader_type note atop
  // this function); a real `173` sets `sawLeaderType` so 171 is treated as draw-order.
  void sawLeaderType;

  return entry;
}

/* ── BLOCKS section structured parser ───────────── */
// Split a raw BLOCKS section group stream into discrete BLOCK records.
// Every BLOCK record starts with `0 BLOCK` and ends with `0 ENDBLK`.
// Entities between the two markers are parsed through the same
// supported/unsupported entity machinery used by the ENTITIES section
// so ATTDEF, INSERT, LINE, CIRCLE, etc. inside a block are exposed with
// their regular shape.
//
// The parser intentionally tolerates leading groups before the first
// BLOCK (stored in `rawBlockGroups`) so unusual files round-trip.
/**
 * Build one DxfBlockDefinition from already-collected header / child /
 * endblk groups.
 *
 * Extracted from {@link parseBlocksSection} so the streaming parser
 * (`./streamingParser.ts`) can pump one block at a time without
 * buffering the whole BLOCKS section as a flat group array.
 *
 * Semantics are identical to the inline body that used to live in
 * parseBlocksSection — ATTRIB+SEQEND grouping, fallback to
 * unsupported-entity on per-entity parse failure, ENDBLK trailing
 * subclass preservation.
 */
export function parseBlockBody(
  headerGroups: DxfGroup[],
  childGroups: DxfGroup[],
  endblkGroups: DxfGroup[],
  stringPool?: StringPool,
): DxfBlockDefinition {
  // Decode header fields. Undocumented group codes are silently dropped
  // here (mirroring legacy behaviour); preservation is the writer's
  // responsibility via the full DXF round-trip path.
  let handle = "";
  let ownerHandle: string | undefined;
  let layer = "0";
  let name = "";
  let flags = 0;
  const basePoint: DxfPoint3D = { x: 0, y: 0, z: 0 };
  let xrefPathName = "";
  let description = "";
  for (const g of headerGroups) {
    switch (g.code) {
      case 5:
        handle = g.value.trim();
        break;
      case 330:
        ownerHandle = g.value.trim();
        break;
      case 8:
        layer = decodeDxfUnicode(g.value); // T9 PR-3 block layer decode
        break;
      case 2:
        if (!name) name = decodeDxfUnicode(g.value.trim()); // T9 PR-3 block name decode
        break;
      case 3:
        // Group 3 inside BLOCK is a duplicate of the block name for
        // xref references. We ignore it if we already have a name.
        if (!name) name = decodeDxfUnicode(g.value.trim()); // T9 PR-3 block name decode
        break;
      case 70:
        flags = Number.parseInt(g.value, 10) || 0;
        break;
      case 10:
        basePoint.x = Number.parseFloat(g.value) || 0;
        break;
      case 20:
        basePoint.y = Number.parseFloat(g.value) || 0;
        break;
      case 30:
        basePoint.z = Number.parseFloat(g.value) || 0;
        break;
      case 1:
        xrefPathName = decodeDxfUnicode(g.value); // T9 PR-3 xref path decode
        break;
      case 4:
        description = decodeDxfUnicode(g.value); // T9 PR-3 block description decode
        break;
      default:
        break;
    }
  }

  // ── Parse child entities with the same pipeline as ENTITIES. ──
  const rawChildEntities: DxfEntity[] = [];
  if (childGroups.length > 0) {
    const childSplits = splitEntities(childGroups);
    for (const entityGroups of childSplits) {
      const header = entityGroups[0];
      if (!header || header.code !== 0) continue;
      if (isSupportedEntityType(header.value)) {
        try {
          rawChildEntities.push(parseSupportedEntity(entityGroups, stringPool));
        } catch {
          // Fall through to unsupported preservation — we must never
          // drop data silently per the full-fidelity rule.
          rawChildEntities.push({
            kind: "unsupported-entity",
            type: header.value,
            groups: entityGroups,
          });
        }
      } else {
        // Round-trip unknown entity types verbatim.
        rawChildEntities.push({
          kind: "unsupported-entity",
          type: header.value,
          groups: entityGroups,
        });
      }
    }
  }

  // Apply the same complex-entity grouping pass as the top-level
  // parseEntitiesSection (shared fold, ./entityGrouping) so a nested INSERT
  // inside a block definition keeps its ATTRIBs and a legacy heavy POLYLINE
  // inside one keeps its VERTEX run.
  const childEntities: DxfEntity[] = foldComplexEntities(rawChildEntities);

  const blockDef: DxfBlockDefinition = {
    name,
    handle,
    layer,
    flags,
    basePoint,
    xrefPathName,
    description,
    entities: childEntities,
    endblkGroups,
  };
  if (ownerHandle !== undefined) blockDef.ownerHandle = ownerHandle;
  return blockDef;
}

export function parseBlocksSection(
  groups: DxfGroup[],
  stringPool?: StringPool,
): DxfBlocksSection {
  const blocks: DxfBlockDefinition[] = [];
  const rawBlockGroups: DxfGroup[] = [];

  let index = 0;

  // Drain any leading non-BLOCK groups verbatim. A well-formed AutoCAD
  // file will not have any but we preserve stray groups so the writer
  // can emit them back without loss.
  while (index < groups.length) {
    const g = groups[index];
    if (!g) break;
    if (g.code === 0 && g.value === "BLOCK") break;
    rawBlockGroups.push(g);
    index += 1;
  }

  while (index < groups.length) {
    const startGroup = groups[index];
    if (!startGroup) break;

    if (startGroup.code !== 0 || startGroup.value !== "BLOCK") {
      // Stray group between blocks — preserve it.
      rawBlockGroups.push(startGroup);
      index += 1;
      continue;
    }

    // ── Collect BLOCK header groups (up to first inner `code 0` which
    //    is either the first child entity or the ENDBLK terminator). ──
    const headerGroups: DxfGroup[] = [];
    index += 1; // consume the `0 BLOCK` header itself
    while (index < groups.length) {
      const g = groups[index];
      if (!g) break;
      if (g.code === 0) break;
      headerGroups.push(g);
      index += 1;
    }

    // ── Collect child entity groups until we hit `0 ENDBLK`. ──
    const childGroups: DxfGroup[] = [];
    while (index < groups.length) {
      const g = groups[index];
      if (!g) break;
      if (g.code === 0 && g.value === "ENDBLK") break;
      childGroups.push(g);
      index += 1;
    }

    // ── Drain the ENDBLK terminator and its trailing subclass groups. ──
    const endblkGroups: DxfGroup[] = [];
    if (index < groups.length) {
      const terminator = groups[index];
      if (
        terminator &&
        terminator.code === 0 &&
        terminator.value === "ENDBLK"
      ) {
        endblkGroups.push(terminator);
        index += 1;
        while (index < groups.length) {
          const g = groups[index];
          if (!g) break;
          if (g.code === 0) break; // next BLOCK or end of section
          endblkGroups.push(g);
          index += 1;
        }
      }
    }

    blocks.push(parseBlockBody(headerGroups, childGroups, endblkGroups, stringPool));
  }

  return {
    kind: "blocks-section",
    blocks,
    rawBlockGroups,
  };
}

/**
 * Whether an ACDBASSOCDEPENDENCY record belongs to the AcDbAssocArray graph,
 * i.e. its owner handle is one of the pre-scanned core array objects. Used by
 * the OBJECTS dispatch to keep generic + dynamic-block-constraint dependencies
 * in `rawObjectGroups` (Codex #251 F1).
 *
 * The membership check is NORMALIZED (Codex #251-A): DXF handles are
 * case-insensitive hex and producers mix cases freely — a record `5 ACT1`
 * with a dependency owner `330 act1` is the SAME edge. `coreHandles` is keyed
 * by `normalizeHandle` (see `computeAssocArrayCoreHandles`), so the lookup
 * must be normalized the same way or the dependency stays in
 * `rawObjectGroups` and the imported array silently loses `sourceEntityIds`.
 * The stored payloads keep the ORIGINAL handle strings — normalization is
 * lookup-only.
 */
function assocDependencyIsArrayOwned(
  groups: DxfGroup[],
  coreHandles: ReadonlySet<string>,
): boolean {
  const { ownerHandle } = readAssocObjectOwner(groups);
  return (
    ownerHandle !== undefined && coreHandles.has(normalizeHandle(ownerHandle))
  );
}

export function parseObjectsSection(groups: DxfGroup[]): DxfObjectsSection {
  const dictionaries: DxfDictionaryEntry[] = [];
  const multileaderStyles: DxfMultileaderStyleEntry[] = [];
  const layouts: DxfLayoutEntry[] = [];
  const dynamicBlockObjects: DxfDynamicBlockObject[] = [];
  // Batch-8 S-ARRAYASSOC-PARSE — AcDbAssocArray graph records pulled out of
  // rawObjectGroups (mirrors dynamicBlockObjects). The second-pass resolver
  // joins them by handle; the writer re-emits each from its rawGroups.
  const assocArrays: DxfAssocArrayObject[] = [];
  const rawObjectGroups: DxfGroup[] = [];
  // N9 AW-0-OBJECTS-BUCKET-ORDER — every record diverted into `assocArrays`
  // ALSO leaves a positional sentinel in `rawObjectGroups`, mirroring the
  // SPATIAL_FILTER precedent (Codex #319 P2, finding 7) exactly:
  // `serializeObjectsSection` / `buildBridgeRawObjectGroups` substitute the
  // placeholder with the re-synthesised record at write time, so a graph
  // interleaved among unsupported OBJECTS records keeps its true position
  // instead of being re-appended after the catch-all (the byte-position
  // residual AW-0 explicitly deferred). One helper for all three diversion
  // sites below keeps the push+sentinel pairing a single truth.
  const pushAssocArray = (entry: DxfAssocArrayObject): void => {
    const index = assocArrays.length;
    assocArrays.push(entry);
    rawObjectGroups.push({
      code: ASSOC_ARRAY_PLACEHOLDER_CODE,
      value:
        entry.handle !== ""
          ? entry.handle
          : `${ASSOC_ARRAY_PLACEHOLDER_INDEX_PREFIX}${index}`,
    });
  };
  // ── Annotative scale infrastructure (PR-2) ──
  // Collected from AcDbScale catalogue records and AcDb*ContextData
  // records as we walk the section. The second-pass resolver in
  // handleResolution.resolveAnnotativeContexts joins them by handle.
  const acadScales: AcDbScale[] = [];
  const annotScaleContexts: NonNullable<DxfObjectsSection["annotScaleContexts"]> = [];
  let acadScaleListDict: DxfObjectsSection["acadScaleListDict"];
  // ── XCLIP / SPATIAL_FILTER infrastructure (EPIC-VIEWPORT-CLIP SLICE 2) ──
  // SPATIAL_FILTER records pulled OUT of rawObjectGroups (mirrors AcDbScale);
  // the writer reconstructs each from this typed list. SLICE 3's resolver
  // joins them to their owning VIEWPORT / block reference by ownerHandle.
  const spatialFilters: DxfSpatialFilter[] = [];

  // Split the OBJECTS section groups into discrete object records. Each
  // record starts with a code 0 header identifying the object type
  // (DICTIONARY, MLEADERSTYLE, XRECORD, LAYOUT, ACDBPLACEHOLDER, ...).
  type ObjectBlock = { type: string; groups: DxfGroup[] };
  const objects: ObjectBlock[] = [];
  let current: ObjectBlock | null = null;

  for (const g of groups) {
    if (g.code === 0) {
      if (current) {
        objects.push(current);
      }
      current = { type: g.value.trim(), groups: [g] };
      continue;
    }
    if (current) {
      current.groups.push(g);
    } else {
      // Stray groups before the first object header — preserve verbatim.
      rawObjectGroups.push(g);
    }
  }
  if (current) {
    objects.push(current);
  }

  // Pre-scan the PROVEN core AcDbAssocArray handles (Codex #251-C): strict
  // ACDBASSOCARRAY* objects (action-body / parameters / item) anchor the set,
  // and a generic ACDBASSOCNETWORK / ACDBASSOCACTION joins ONLY when the
  // reachability fixpoint proves it connected to an array object (an action
  // that owns / points at an ACDBASSOCARRAYACTIONBODY*, a network that owns
  // such an action). A dependency record is diverted into the typed
  // `assocArrays[]` ONLY when its owner is in this proven set. Generic
  // networks/actions + their dynamic-block-constraint ACDBASSOCDEPENDENCY
  // records remain in `rawObjectGroups` so `collectAssocConstraintRecords`
  // still sees every depChain it does today (Codex #251 F1/-C — no regression
  // to constraint import). Set keys are NORMALIZED handles (Codex #251-A) —
  // every membership check below must normalize its lookup.
  const assocArrayCoreHandles = computeAssocArrayCoreHandles(objects);

  for (const obj of objects) {
    if (obj.type === "DICTIONARY" || obj.type === "ACDBDICTIONARYWDFLT") {
      const dict = parseDictionaryEntry(obj.groups, obj.type);
      dictionaries.push(dict);
      // Detect ACAD_SCALELIST dict by looking at the first 100 marker
      // (subclass) — for any DICTIONARY whose body lists named scale
      // entries; we cross-reference by also checking that the entries
      // resolve to AcDbScale records owned by this dict in the second
      // pass. Cheaper-but-imperfect fast path: name-based heuristic via
      // the `extraGroups` is not available on DxfDictionaryEntry, so we
      // capture the dict's body shape by reparsing into the typed entry
      // when we encounter it later. For now we capture via DICTIONARY
      // owner walk in resolver — keep the typed entry empty unless a
      // child object explicitly identifies the dict.
      // PR-5/round-trip: writer reconstructs ACAD_SCALELIST from the
      // typed acadScales[] catalogue plus ownership chain in the dicts.
      void dict;
    } else if (obj.type === "MLEADERSTYLE") {
      multileaderStyles.push(parseMultileaderStyleEntry(obj.groups));
    } else if (obj.type === "LAYOUT") {
      layouts.push(parseLayoutEntry(obj.groups));
    } else if (isAcDbScaleType(obj.type, obj.groups)) {
      // AcDbScale catalogue entry. Decode and add to the catalogue. We
      // intentionally DO NOT push these into rawObjectGroups — the
      // writer reconstructs them from the typed list to keep the OBJECTS
      // section semantically clean.
      acadScales.push(parseAcDbScale(obj.groups));
    } else if (isAnnotScaleContextDataType(obj.type, obj.groups)) {
      // Per-scale context-data record. Dispatch to the typed parser; on
      // success store the typed envelope. On null (unknown subclass
      // chain) fall through to rawObjectGroups verbatim.
      const parsed: ParsedAnnotativeRecord | null =
        dispatchObjectsAnnotativeRecord(obj.type, obj.groups);
      if (parsed) {
        annotScaleContexts.push({
          handle: parsed.handle,
          ownerHandle: parsed.ownerHandle,
          scaleHandle: parsed.scaleHandle,
          subclass: parsed.subclass,
          context: parsed.context,
        });
      } else {
        for (const g of obj.groups) rawObjectGroups.push(g);
      }
    } else if (isSpatialFilterType(obj.type, obj.groups)) {
      // SPATIAL_FILTER (AcDbSpatialFilter) — XCLIP / VPCLIP boundary record
      // (EPIC-VIEWPORT-CLIP SLICE 2). Decode the typed clip-boundary +
      // block→WCS transforms. Like AcDbScale the full group list is NOT pushed
      // into rawObjectGroups — the writer reconstructs the record from the
      // typed list. The second-pass resolver (SLICE 3, resolveViewportClip)
      // joins each to its owning entity via ownerHandle.
      //
      // Codex #319 P2, finding 7 — OBJECTS-section interleave. A SPATIAL_FILTER
      // can sit BETWEEN unsupported OBJECTS records (which round-trip verbatim
      // through rawObjectGroups). Emitting all spatialFilters[] ahead of the
      // rawObjectGroups catch-all would hoist it out of position. Push a single
      // PLACEHOLDER SENTINEL into rawObjectGroups AT THIS POSITION carrying the
      // index into spatialFilters[]; `serializeObjectsSection` substitutes the
      // re-synthesised record's groups for the sentinel so the relative order is
      // preserved. The sentinel code is negative (real DXF codes are
      // non-negative) so it never collides — see SPATIAL_FILTER_PLACEHOLDER_CODE.
      const sfIndex = spatialFilters.length;
      const sfEntry = parseSpatialFilterEntry(obj.groups);
      spatialFilters.push(sfEntry);
      rawObjectGroups.push({
        code: SPATIAL_FILTER_PLACEHOLDER_CODE,
        // STABLE key = the record's own DXF handle (group 5), so the writer
        // resolves the right filter even if a later consumer (SLICE-3) reorders
        // or filters section.spatialFilters[] — a positional index would go
        // stale (Codex pre-PR MEDIUM). A handle-less (malformed/synthetic)
        // record falls back to its positional index, prefixed so it can never
        // collide with a real hex handle.
        value:
          sfEntry.handle !== ""
            ? sfEntry.handle
            : `${SPATIAL_FILTER_PLACEHOLDER_INDEX_PREFIX}${sfIndex}`,
      });
    } else if (isAssocArrayStrictObjectType(obj.type)) {
      // Strict associative-array graph objects (ACDBASSOCARRAY* action-body /
      // parameters / item) — array-specific by class name. Decode the typed
      // handle/owner/params here and keep the full group list for verbatim
      // round-trip; the second-pass resolver (resolveAssocArrays) joins them.
      // Pulled OUT of rawObjectGroups (mirrors dynamicBlockObjects) — the
      // writer re-emits each from its rawGroups.
      pushAssocArray(decodeAssocArrayObject(obj.type, obj.groups));
    } else if (
      isAssocArrayGraphCandidateType(obj.type) &&
      assocArrayCoreHandles.has(
        normalizeHandle(readAssocObjectOwner(obj.groups).handle),
      )
    ) {
      // ACDBASSOCNETWORK / ACDBASSOCACTION are GENERIC ObjectARX associative
      // classes (also back assoc dimensions / surfaces / dynamic-block
      // constraints). Divert into the array bucket ONLY when the reachability
      // pre-scan proved this record connected to an ACDBASSOCARRAY* object
      // (Codex #251-C). A generic network/action falls through to
      // rawObjectGroups below so the constraint importer keeps its graph.
      pushAssocArray(decodeAssocArrayObject(obj.type, obj.groups));
    } else if (
      obj.type.trim().toUpperCase() === "ACDBASSOCDEPENDENCY" &&
      assocDependencyIsArrayOwned(obj.groups, assocArrayCoreHandles)
    ) {
      // ACDBASSOCDEPENDENCY is a SHARED object type. Divert it into the array
      // bucket ONLY when its owner is a PROVEN core array object (reachable
      // from the array graph; owner lookup normalized — Codex #251-A).
      // Generic + constraint dependencies fall through to the dynamic-block /
      // rawObjectGroups branches below so `collectAssocConstraintRecords`
      // still sees them (Codex #251 F1).
      pushAssocArray(decodeAssocArrayObject(obj.type, obj.groups));
    } else if (isSupportedDynamicBlockType(obj.type)) {
      // Dynamic block authoring objects — parse the handle + owner here,
      // keep the full group list for round-trip, and let the bridge
      // (dxfBridge.dynamic.ts) extract typed fields downstream.
      dynamicBlockObjects.push(parseDynamicBlockObject(obj.type, obj.groups));
    } else {
      // Unknown object kinds round-trip verbatim through rawObjectGroups.
      // NOTE: avoid `rawObjectGroups.push(...obj.groups)` — real-world DXF
      // files (e.g. Аэротенк.dxf) can carry single objects with 400k+
      // groups (SORTENTSTABLE for draw-order of 685k entities), and
      // `Array.push(...huge)` overflows V8's argument-count limit and
      // surfaces as "Maximum call stack size exceeded". Same fix as in
      // writer.ts:1428.
      for (const g of obj.groups) rawObjectGroups.push(g);
    }
  }

  // Identify the ACAD_SCALELIST DICTIONARY (if any) by scanning the
  // parsed catalogue's owner handles. Every AcDbScale points at its
  // parent dict via group 330 — when all (or most) AcDbScale records
  // share the same ownerHandle, that handle is the SCALELIST dict.
  if (acadScales.length > 0) {
    const ownerCounts = new Map<string, number>();
    for (const s of acadScales) {
      if (!s.ownerHandle) continue;
      ownerCounts.set(s.ownerHandle, (ownerCounts.get(s.ownerHandle) ?? 0) + 1);
    }
    let scaleListHandle: string | undefined;
    let bestCount = 0;
    for (const [h, c] of ownerCounts) {
      if (c > bestCount) {
        bestCount = c;
        scaleListHandle = h;
      }
    }
    if (scaleListHandle) {
      // Re-locate the dict's raw groups via dictionaries[] and decode
      // into the typed scale-list shape. We still keep the generic
      // DxfDictionaryEntry in dictionaries[] for round-trip — the typed
      // entry below is an auxiliary projection consumed by the resolver
      // and writer.
      const dictEntry = dictionaries.find(
        (d) => d.handle === scaleListHandle,
      );
      if (dictEntry) {
        // parseAcDbScaleListDict expects raw groups but we only have
        // the typed entries[] here. Reconstruct a minimal projection
        // sufficient for the scale-list use case: handle + the (name,
        // handle) pairs already decoded by parseDictionaryEntry.
        acadScaleListDict = {
          handle: dictEntry.handle,
          scales: dictEntry.entries.map((e) => ({
            name: e.name,
            handle: e.handle,
          })),
        };
        // SL-16 P3 (Codex P2) — the typed SCALELIST projection MUST carry the
        // same `330` owner + `102 {ACAD_REACTORS}` persistent-reactor metadata
        // the generic DxfDictionaryEntry already captured (parseDictionaryEntry
        // decodes both — owner from the 330 OUTSIDE any 102 block, reactors from
        // the 330s INSIDE `{ACAD_REACTORS`). REUSE that already-parsed entry — do
        // NOT re-parse the raw groups. Without this a consumer reading the typed
        // projection (not the dictionaries[] copy) sees owner/reactors as
        // undefined, and a projection-channel re-export (serializeAcDbScaleListDict)
        // emits an orphaned SCALELIST → ezdxf audit flags INVALID_OWNER_HANDLE.
        if (dictEntry.ownerHandle) {
          acadScaleListDict.ownerHandle = dictEntry.ownerHandle;
        }
        if (dictEntry.reactorHandles && dictEntry.reactorHandles.length > 0) {
          acadScaleListDict.reactorHandles = dictEntry.reactorHandles;
        }
      }
    }
  }
  // Hint for downstream consumers — acknowledge the helper that operates
  // on raw groups (used by tests and the writer round-trip path).
  void parseAcDbScaleListDict;

  const section: DxfObjectsSection = {
    kind: "objects-section",
    dictionaries,
    multileaderStyles,
    layouts,
    dynamicBlockObjects,
    rawObjectGroups,
  };
  if (assocArrays.length > 0) section.assocArrays = assocArrays;
  if (acadScales.length > 0) section.acadScales = acadScales;
  if (annotScaleContexts.length > 0) {
    section.annotScaleContexts = annotScaleContexts;
  }
  if (acadScaleListDict) section.acadScaleListDict = acadScaleListDict;
  if (spatialFilters.length > 0) section.spatialFilters = spatialFilters;
  return section;
}

/**
 * Minimal shallow parser for dynamic-block-authoring OBJECTS. We decode
 * only the handle (code 5) and owner handle (code 330) so the bridge
 * layer can wire objects together; every group — including 0/100 markers
 * — is preserved in `rawGroups` so the writer can emit the object back
 * byte-for-byte. Typed decoding of parameter / action fields happens in
 * `dxfBridge.dynamic.ts`.
 *
 * DYN-4 Phase 7 — real AutoCAD 2018+ constraint-based blocks wrap the
 * `330 ownerHandle` group in a `{ACAD_REACTORS` … `}` control block
 * which itself uses `330` for REACTOR references:
 *
 *     0   BLOCKHORIZONTALCONSTRAINTPARAMETER
 *     5   4E34                   ← handle
 *     102 {ACAD_REACTORS
 *     330 4E13                   ← reactor, NOT the owner
 *     330 4E3B                   ← reactor, NOT the owner
 *     330 4E9B                   ← reactor, NOT the owner
 *     102 }
 *     330 4E33                   ← real ownerHandle (ACAD_EVALUATION_GRAPH)
 *     …
 *
 * Picking the first 330 unconditionally assigns `ownerHandle=4E13`
 * which is a reactor, breaking downstream BFS from the eval_graph. Track
 * the `{…}` control depth via group 102 and only consume 330 groups at
 * depth 0. This mirrors the "skip control groups" logic in
 * `parseDictionaryEntry` and preserves round-trip fidelity because the
 * raw groups still carry the full sequence verbatim.
 */
export function parseDynamicBlockObject(
  type: string,
  groups: DxfGroup[],
): DxfDynamicBlockObject {
  let handle = "";
  let ownerHandle: string | undefined;
  let controlDepth = 0;
  for (const g of groups) {
    const nextDepth = advanceControlDepth(g, controlDepth);
    if (nextDepth !== undefined) {
      controlDepth = nextDepth;
      continue;
    }
    if (g.code === 5 && handle === "") {
      handle = g.value.trim();
      continue;
    }
    // Only accept a `330 ownerHandle` at control-group depth 0. Depth > 0
    // means we are inside `{ACAD_REACTORS`/`{BLKREFS`/another 102-block
    // whose 330 references are cross-links, not the owner.
    if (g.code === 330 && ownerHandle === undefined && controlDepth === 0) {
      ownerHandle = g.value.trim();
    }
  }
  const result: DxfDynamicBlockObject = {
    kind: "dynamic-block-object",
    type,
    handle,
    rawGroups: groups.slice(),
  };
  if (ownerHandle !== undefined) result.ownerHandle = ownerHandle;
  return result;
}

/**
 * Parse a single LAYOUT object record (OBJECTS section entry). LAYOUT
 * stores paper-space tab data — the name shown on the tab bar, plot
 * settings, and extent/limits. Handle code 330 points to the owning
 * BLOCK_RECORD (`*Paper_Space`/`*Paper_Space<N>`) and is mirrored back
 * by the BLOCK_RECORD's group 340.
 *
 * The LAYOUT object is split into two 100-subclass blocks:
 *
 *   100 AcDbPlotSettings — the embedded PLOTSETTINGS sub-object:
 *     1 = page-setup name   4 = paper size       6 = plot view name
 *     40/41/42/43 = unprintable margins (L/B/R/T, mm)
 *     44/45 = physical paper width/height (mm)
 *     46/47 = plot offset   48/49/140/141 = plot window rect
 *     70 = plot layout flags (bitfield)   72 = plot paper units
 *     73 = plot rotation    74 = plot-area type  142/143 = custom scale
 *     7  = current plot-style sheet (CTB/STB) name
 *
 *   100 AcDbLayout — the layout block:
 *     1 = tab name          70 = layout flags    71 = tab order
 *     10/20 = min limits    11/21 = max limits
 *     12/22/32 = insertion base   14/24/34 = min extents
 *     15/25/35 = max extents      76 = ortho view  146 = elevation
 *
 *   5 = handle    330 = owner handle (BLOCK_RECORD)
 *
 * Codes 1, 6, 70 and 76 appear in BOTH subclass blocks with different
 * meanings, so the parser tracks the active 100-marker (`subclass`) to
 * disambiguate them. Everything before the first AcDbLayout marker is
 * PLOTSETTINGS; everything after is the layout block.
 */
export function parseLayoutEntry(groups: DxfGroup[]): DxfLayoutEntry {
  const entry: DxfLayoutEntry = {
    kind: "layout",
    handle: "",
    name: "",
  };
  const extraGroups: DxfGroup[] = [];
  // FIX E (round-9 #4) — a nested `102` reactor / xdictionary block in the LAYOUT
  // HEADER slot (before the first `100` subclass marker, right after the 5/330
  // owner) must round-trip BYTE-FAITHFULLY at its ORIGINAL position. The generic
  // `extraGroups` are re-emitted at the END of the record by `serializeLayoutEntry`,
  // so a header-slot 102 block would be relocated to the tail on import→export.
  // We route those header-slot 102-nested groups into a SEPARATE positional bucket
  // the writer re-emits in their original slot; only `102` blocks that appear AFTER
  // the first 100 marker fall through to `extraGroups` (their tail position is the
  // canonical AutoCAD slot for post-subclass reactors, already byte-faithful).
  const headerExtraGroups: DxfGroup[] = [];
  // True once the first `100` subclass marker has been seen — header-slot 102
  // blocks are those captured while this is still false.
  let sawSubclassMarker = false;
  // Staging scratch for grouped (10+20+30 style) point values.
  let pendingMinLimitX: number | undefined;
  let pendingMaxLimitX: number | undefined;
  let pendingInsertionBaseX: number | undefined;
  let pendingInsertionBaseY: number | undefined;
  let pendingMinExtentsX: number | undefined;
  let pendingMinExtentsY: number | undefined;
  let pendingMaxExtentsX: number | undefined;
  let pendingMaxExtentsY: number | undefined;
  let pendingPlotOriginX: number | undefined;
  // Plot-window staging (48/49 lower-left, 140/141 upper-right).
  let pendingWindowLLX: number | undefined;
  let pendingWindowLLY: number | undefined;
  let pendingWindowURX: number | undefined;
  // Margin / scale staging.
  let marginL: number | undefined;
  let marginB: number | undefined;
  let marginR: number | undefined;
  let scaleNum: number | undefined;
  // Active 100-subclass marker: "plot" until the AcDbLayout marker is seen.
  // AutoCAD always writes AcDbPlotSettings first, so this is the correct
  // initial state even if the explicit marker is absent (defensive).
  let subclass: "plot" | "layout" = "plot";
  // Codex round-4 P2b — `102` nested-block depth (reactors / xdictionary).
  // Mirrors the table-record 102-nesting guard (lines ~389-418): `102/{...`
  // opens a block, `102/}` closes it. While depth > 0 EVERY group (including the
  // `102` boundary markers) is preserved verbatim in `extraGroups`, in original
  // order, so the writer re-emits the nested block byte-for-byte. Only a
  // TOP-LEVEL `330` (outside any open `102` block) reaches the owner / dictionary
  // owner assignment below — an inner reactor `330` must never masquerade as the
  // layout owner or dictionary owner.
  let nestingDepth = 0;

  for (const g of groups) {
    const code = g.code;
    const raw = g.value;
    // Codex round-4 P2b — capture nested `102` blocks (reactors / xdictionary)
    // VERBATIM, mirroring the table-record guard (lines ~389-418). Match the same
    // semantics: an opening marker value starts with "{", the close is "}".
    if (code === 102) {
      const marker = raw.trim();
      if (marker.startsWith("{")) nestingDepth++;
      else if (nestingDepth > 0) nestingDepth--; // a closing "}" marker
      // FIX E — a HEADER-slot 102 block (before the first 100 marker) goes to the
      // positional bucket so the writer re-emits it in its original slot; a
      // post-subclass 102 block falls through to `extraGroups` (tail = canonical).
      if (!sawSubclassMarker) headerExtraGroups.push(g);
      else extraGroups.push(g);
      continue;
    }
    if (nestingDepth > 0) {
      // Inside a nested 102 block (reactors / xdictionary) — preserve verbatim,
      // never let an inner 330 masquerade as the layout owner / dictionary owner.
      if (!sawSubclassMarker) headerExtraGroups.push(g);
      else extraGroups.push(g);
      continue;
    }
    if (code === 0) continue; // LAYOUT header
    if (code === 100) {
      // Subclass-marker switch — drives disambiguation of 1/6/70/76.
      sawSubclassMarker = true;
      if (raw.trim() === "AcDbLayout") subclass = "layout";
      else if (raw.trim() === "AcDbPlotSettings") subclass = "plot";
      continue;
    }
    if (code === 5) {
      entry.handle = raw.trim();
      continue;
    }
    if (code === 330) {
      // A LAYOUT can carry up to TWO 330 owner pointers: the ACAD_LAYOUT
      // dictionary owner (in the AcDbPlotSettings-leading context) and the
      // owning BLOCK_RECORD (in the AcDbLayout subclass). `ownerHandle` is the
      // BLOCK_RECORD link (what convertRawLayout consumes for
      // `ownerBlockRecordHandle`).
      //
      // Disambiguation that PRESERVES the legacy single-330 contract: a 330 in
      // the AcDbLayout subclass is unambiguously the BLOCK_RECORD → set
      // `ownerHandle` and DEMOTE any earlier plot-context 330 to
      // `dictionaryOwnerHandle`. A 330 seen ONLY in the plot context (the common
      // single-330 shape — a LAYOUT whose sole owner pointer is the block
      // record, as in many real files + the existing fixtures) stays
      // `ownerHandle`. (ROOT C — the AcDbLayout 330 is what ezdxf pairs
      // LAYOUT↔BLOCK_RECORD by; the writer re-emits both in order.)
      if (subclass === "layout") {
        // Promote: this is the real BLOCK_RECORD link; the prior plot-context
        // 330 (if any) was the dictionary owner.
        if (entry.ownerHandle !== undefined) {
          entry.dictionaryOwnerHandle = entry.ownerHandle;
        }
        entry.ownerHandle = raw.trim();
      } else {
        // Plot context — provisionally the owner (legacy single-330 shape). If
        // an AcDbLayout 330 follows, the branch above demotes this to the
        // dictionary owner.
        entry.ownerHandle = raw.trim();
      }
      continue;
    }
    if (code === 1) {
      // PLOTSETTINGS → page-setup name; AcDbLayout → tab name.
      // T9 PR-3 — both are `\U+`-escaped on write (code 1 ∈ allowlist); decode.
      if (subclass === "plot") entry.pageSetupName = decodeDxfUnicode(raw);
      else entry.name = decodeDxfUnicode(raw);
      continue;
    }
    if (code === 6) {
      // PLOTSETTINGS → plot view name. (Not used under AcDbLayout.)
      if (subclass === "plot") entry.plotViewName = decodeDxfUnicode(raw); // T9 PR-3 decode
      else extraGroups.push(g);
      continue;
    }
    if (code === 4) {
      entry.paperSize = decodeDxfUnicode(raw); // T9 PR-3 — paper size name decode
      continue;
    }
    if (code === 7) {
      // Current plot-style sheet name (CTB/STB) — PLOTSETTINGS only.
      entry.plotStyleName = decodeDxfUnicode(raw); // T9 PR-3 decode
      continue;
    }
    if (code === 72) {
      const n = Number.parseInt(raw, 10);
      if (Number.isFinite(n)) entry.plotPaperUnits = n;
      continue;
    }
    if (code === 73) {
      const n = Number.parseInt(raw, 10);
      if (Number.isFinite(n)) entry.plotRotation = n;
      continue;
    }
    if (code === 74) {
      const n = Number.parseInt(raw, 10);
      if (Number.isFinite(n)) entry.plotAreaType = n;
      continue;
    }
    if (code === 71) {
      const n = Number.parseInt(raw, 10);
      if (Number.isFinite(n)) entry.tabOrder = n;
      continue;
    }
    if (code === 70) {
      const n = Number.parseInt(raw, 10);
      // PLOTSETTINGS → plot layout flags; AcDbLayout → layout flags
      // (round-tripped verbatim via extraGroups, no model field today).
      if (subclass === "plot") {
        if (Number.isFinite(n)) entry.plotLayoutFlags = n;
      } else {
        extraGroups.push(g);
      }
      continue;
    }
    if (code === 76) {
      // PLOTSETTINGS standard-scale type (75 is preceding) is not parsed;
      // 76 under AcDbLayout is the orthographic view type.
      if (subclass === "layout") {
        const n = Number.parseInt(raw, 10);
        if (Number.isFinite(n)) entry.orthographicViewType = n;
      } else {
        extraGroups.push(g);
      }
      continue;
    }
    // Physical paper dimensions (PLOTSETTINGS 44/45).
    if (code === 44) {
      const n = Number.parseFloat(raw);
      if (Number.isFinite(n)) entry.paperWidthMm = n;
      continue;
    }
    if (code === 45) {
      const n = Number.parseFloat(raw);
      if (Number.isFinite(n)) entry.paperHeightMm = n;
      continue;
    }
    // Unprintable margins (PLOTSETTINGS 40/41/42/43).
    if (code === 40) {
      marginL = Number.parseFloat(raw);
      continue;
    }
    if (code === 41) {
      marginB = Number.parseFloat(raw);
      continue;
    }
    if (code === 42) {
      marginR = Number.parseFloat(raw);
      continue;
    }
    if (code === 43) {
      const top = Number.parseFloat(raw);
      if (
        marginL !== undefined &&
        marginB !== undefined &&
        marginR !== undefined &&
        Number.isFinite(top)
      ) {
        entry.plotMargins = {
          left: marginL,
          bottom: marginB,
          right: marginR,
          top,
        };
      }
      marginL = undefined;
      marginB = undefined;
      marginR = undefined;
      continue;
    }
    // Plot window rect (PLOTSETTINGS 48/49 lower-left, 140/141 upper-right).
    if (code === 48) {
      pendingWindowLLX = Number.parseFloat(raw);
      continue;
    }
    if (code === 49) {
      pendingWindowLLY = Number.parseFloat(raw);
      continue;
    }
    if (code === 140) {
      pendingWindowURX = Number.parseFloat(raw);
      continue;
    }
    if (code === 141) {
      const ury = Number.parseFloat(raw);
      if (
        pendingWindowLLX !== undefined &&
        pendingWindowLLY !== undefined &&
        pendingWindowURX !== undefined &&
        Number.isFinite(ury)
      ) {
        entry.plotWindow = {
          lowerLeftX: pendingWindowLLX,
          lowerLeftY: pendingWindowLLY,
          upperRightX: pendingWindowURX,
          upperRightY: ury,
        };
      }
      pendingWindowLLX = undefined;
      pendingWindowLLY = undefined;
      pendingWindowURX = undefined;
      continue;
    }
    // Custom print scale (PLOTSETTINGS 142 numerator / 143 denominator).
    if (code === 142) {
      scaleNum = Number.parseFloat(raw);
      continue;
    }
    if (code === 143) {
      const den = Number.parseFloat(raw);
      if (scaleNum !== undefined && Number.isFinite(den)) {
        entry.plotScale = { numerator: scaleNum, denominator: den };
      }
      scaleNum = undefined;
      continue;
    }
    if (code === 146) {
      const n = Number.parseFloat(raw);
      if (Number.isFinite(n)) entry.elevation = n;
      continue;
    }
    // Plot origin — groups 46 (x), 47 (y).
    if (code === 46) {
      pendingPlotOriginX = Number.parseFloat(raw);
      continue;
    }
    if (code === 47) {
      const y = Number.parseFloat(raw);
      if (pendingPlotOriginX !== undefined && Number.isFinite(y)) {
        entry.plotOrigin = { x: pendingPlotOriginX, y };
      }
      pendingPlotOriginX = undefined;
      continue;
    }
    // Limits (2D): 10/20 (min), 11/21 (max).
    if (code === 10) {
      pendingMinLimitX = Number.parseFloat(raw);
      continue;
    }
    if (code === 20) {
      const y = Number.parseFloat(raw);
      if (pendingMinLimitX !== undefined && Number.isFinite(y)) {
        entry.minimumLimits = { x: pendingMinLimitX, y };
      }
      pendingMinLimitX = undefined;
      continue;
    }
    if (code === 11) {
      pendingMaxLimitX = Number.parseFloat(raw);
      continue;
    }
    if (code === 21) {
      const y = Number.parseFloat(raw);
      if (pendingMaxLimitX !== undefined && Number.isFinite(y)) {
        entry.maximumLimits = { x: pendingMaxLimitX, y };
      }
      pendingMaxLimitX = undefined;
      continue;
    }
    // Insertion base (3D): 12/22/32.
    if (code === 12) {
      pendingInsertionBaseX = Number.parseFloat(raw);
      continue;
    }
    if (code === 22) {
      pendingInsertionBaseY = Number.parseFloat(raw);
      continue;
    }
    if (code === 32) {
      const z = Number.parseFloat(raw);
      if (
        pendingInsertionBaseX !== undefined &&
        pendingInsertionBaseY !== undefined &&
        Number.isFinite(z)
      ) {
        entry.insertionBase = {
          x: pendingInsertionBaseX,
          y: pendingInsertionBaseY,
          z,
        };
      }
      pendingInsertionBaseX = undefined;
      pendingInsertionBaseY = undefined;
      continue;
    }
    // Min extents: 14/24/34.
    if (code === 14) {
      pendingMinExtentsX = Number.parseFloat(raw);
      continue;
    }
    if (code === 24) {
      pendingMinExtentsY = Number.parseFloat(raw);
      continue;
    }
    if (code === 34) {
      const z = Number.parseFloat(raw);
      if (
        pendingMinExtentsX !== undefined &&
        pendingMinExtentsY !== undefined &&
        Number.isFinite(z)
      ) {
        entry.minimumExtents = {
          x: pendingMinExtentsX,
          y: pendingMinExtentsY,
          z,
        };
      }
      pendingMinExtentsX = undefined;
      pendingMinExtentsY = undefined;
      continue;
    }
    // Max extents: 15/25/35.
    if (code === 15) {
      pendingMaxExtentsX = Number.parseFloat(raw);
      continue;
    }
    if (code === 25) {
      pendingMaxExtentsY = Number.parseFloat(raw);
      continue;
    }
    if (code === 35) {
      const z = Number.parseFloat(raw);
      if (
        pendingMaxExtentsX !== undefined &&
        pendingMaxExtentsY !== undefined &&
        Number.isFinite(z)
      ) {
        entry.maximumExtents = {
          x: pendingMaxExtentsX,
          y: pendingMaxExtentsY,
          z,
        };
      }
      pendingMaxExtentsX = undefined;
      pendingMaxExtentsY = undefined;
      continue;
    }
    // Unrecognised group — preserve for round-trip.
    extraGroups.push(g);
  }
  if (headerExtraGroups.length > 0) entry.headerExtraGroups = headerExtraGroups;
  if (extraGroups.length > 0) entry.extraGroups = extraGroups;
  return entry;
}

/* ── Two-pass handle resolution ───────────────────── */

// Walk every parsed MLEADERSTYLE and try to resolve its handle-based
// references (textStyle, linetype, arrowhead block, content block)
// against the indexes we built during pass 1. Unresolved handles are
// silently left as undefined — the bridge layer will fall back to
// sensible defaults. This happens once per parseDxfDocument call and
// is a no-op when the OBJECTS section is empty.
